import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, Users, Lock } from 'lucide-react';
import Header from '../components/Header';
import GlowBackground from '../components/GlowBackground';
import { useAuth } from '../context/AuthContext';
import BasicInfoSection from '../components/profile/BasicInfoSection';
import ProfileSectionGeneric from '../components/profile/ProfileSectionGeneric';
import WorkingWithMeSection from '../components/profile/WorkingWithMeSection';
import CultureDiarySection from '../components/profile/CultureDiarySection';
import {
  WORK_RHYTHM_ROWS, WHAT_I_BRING_ROWS, OUTSIDE_ROWS,
} from '../components/profile/constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, isAuthenticated } = useAuth();

  const [profile, setProfile] = useState(null);
  const [myProfile, setMyProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isOwn = !userId || (user && userId === user.id);
  const readOnly = !isOwn;

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const url = isOwn ? `${API}/profile/me` : `${API}/profile/${userId}`;
    axios.get(url)
      .then(r => setProfile(r.data))
      .catch(err => {
        setError(err?.response?.data?.detail || 'Could not load profile.');
      })
      .finally(() => setLoading(false));
  }, [userId, user, isOwn, authLoading, isAuthenticated]);

  // Viewing a teammate: also load own scales so we can overlay a comparison.
  useEffect(() => {
    if (authLoading || !isAuthenticated || isOwn) {
      setMyProfile(null);
      return;
    }
    axios.get(`${API}/profile/me`)
      .then(r => setMyProfile(r.data))
      .catch(() => setMyProfile(null));
  }, [userId, isOwn, authLoading, isAuthenticated]);

  const updateBasic = async (payload) => {
    const res = await axios.put(`${API}/profile/me`, payload);
    setProfile(res.data);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FFF8F0' }}>
        <div className="w-10 h-10 rounded-full border-2 border-orange-primary/30 border-t-orange-primary animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FFF8F0' }}>
        <Header />
        <div className="flex-1 flex items-center justify-center px-6 text-center">
          <div>
            <Lock size={32} className="mx-auto mb-3 text-charcoal/30" />
            <p className="text-charcoal/60 mb-4">Sign in to view your profile.</p>
            <Link to="/" className="text-orange-primary hover:underline text-sm">
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FFF8F0' }}>
        <Header />
        <div className="flex-1 flex items-center justify-center px-6 text-center">
          <div>
            <p className="text-charcoal/60 mb-4">{error}</p>
            <Link to="/" className="text-orange-primary hover:underline text-sm">
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative" style={{ backgroundColor: '#FFF8F0' }}>
      <GlowBackground />
      <Header />

      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Back + directory links */}
        <div className="flex items-center justify-between mb-6">
          <button
            type="button"
            onClick={() => {
              // navigate(-1) does nothing on a freshly-opened tab.
              // Fall back to home if there's no history to go back to.
              if (window.history.length > 1) navigate(-1);
              else navigate('/');
            }}
            data-testid="profile-back"
            className="inline-flex items-center gap-1.5 text-sm text-charcoal/60 hover:text-charcoal transition-colors"
          >
            <ArrowLeft size={14} /> Back
          </button>
          {user?.is_org_member && (
            <Link
              to="/directory"
              data-testid="go-to-directory"
              className="inline-flex items-center gap-1.5 text-sm text-orange-primary hover:underline"
            >
              <Users size={14} /> Team directory
            </Link>
          )}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="space-y-5"
        >
          <BasicInfoSection
            profile={profile}
            organisation={profile?.organisation}
            displayName={profile?.display_name}
            email={isOwn ? profile?.email : null}
            readOnly={readOnly}
            hidePrivate={readOnly}      // DoB + gender always hidden from teammates
            onSave={updateBasic}
          />

          <ProfileSectionGeneric
            title="My Work Rhythm"
            subtitle="How and when I work best."
            rows={WORK_RHYTHM_ROWS}
            profile={profile}
            readOnly={readOnly}
            onProfileUpdate={setProfile}
            testId="section-work-rhythm"
          />

          <ProfileSectionGeneric
            title="What I Bring"
            subtitle="My strengths, growth areas, and what motivates me."
            rows={WHAT_I_BRING_ROWS}
            profile={profile}
            readOnly={readOnly}
            onProfileUpdate={setProfile}
            testId="section-what-i-bring"
          />

          <ProfileSectionGeneric
            title="Outside of Work"
            subtitle="A bit of context beyond the role."
            rows={OUTSIDE_ROWS}
            profile={profile}
            readOnly={readOnly}
            onProfileUpdate={setProfile}
            testId="section-outside-of-work"
          />

          <WorkingWithMeSection
            profile={profile}
            readOnly={readOnly}
            onProfileUpdate={setProfile}
            myProfile={myProfile}
            compareName={profile?.first_name || profile?.display_name || 'them'}
          />

          <CultureDiarySection
            profile={profile}
            userId={isOwn ? user?.id : userId}
            isOwn={isOwn}
            organisation={profile?.organisation}
            onProfileUpdate={setProfile}
          />
        </motion.div>
      </main>
    </div>
  );
};

export default Profile;
